// @ts-strict-ignore
/** @jsx jsx */
import { jsx } from 'theme-ui'
import React from 'react'
import customValidations from '../helpers/form-validations'
import { SimpleInput } from '../helpers/simple-input'
import customFunctions from '../helpers/form-validation-functions'
import { Field, RadioGroup, Icon } from '../../atoms'
import SimpleDropDown from '../address-input/simple-drop-down'
import InputAlert from '../helpers/input-alert'
import AddressHistoryUswitch from '../address-lookup'
import AddressInput from '../address-input'
import InputMask from 'react-input-mask'
import { formatStringToLocale } from '../../utils/number-formatter'
import { Checkbox, Tooltip } from '@uswitch/spark'
import { useComponentTracking } from '../../utils/component-events'
import { getConsentText, getExistingConsent } from '@uswitch/rvuconsent'
import classNames from 'classnames'

const masks = {
  currency: 'currency',
  numeric: 'numeric',
  number: '9,999,999',
  date: '99/99/9999',
}

const isValidNumber = (value) => /^\d+$/.test(value)

const FormStepField = ({
  field,
  register,
  errors,
  setValue,
  getValues,
  variant = 'default',
  watch,
  goToPage,
  updateCompletionState,
  onKeyDownFunction,
  trackInteraction = () => {},
  sectionTitle,
  index,
  addressLookup,
  isChild,
  defaultValues,
  fieldTracking,
}) => {
  const { validation: { custom, customFunction, ...rest } = {} } = field
  let {
    validation,
    name,
    type,
    children,
    placeholder,
    label,
    options = [],
    inputMask: { customMask, mask } = {},
    value = '',
    prefix,
    suffix,
    text,
    links,
    uppercase,
    description,
    inputSize,
    title,
    consentType = null,
    consentName = null,
    fieldVariant,
    subtext,
    minimumRequiredYears,
    privacyPolicyLink,
    tooltip,
    tooltipText,
    isVisible,
    watchForChanges = false,
    visibleIf,
  } = field

  const trackComponent = useComponentTracking()

  const isSingleRow = fieldVariant === 'singleRow'

  if (custom && customValidations[custom]) {
    validation = {
      ...rest,
      ...customValidations[custom],
    }
  }

  const [consentObj, setConsentObj] = React.useState(null)

  React.useEffect(() => {
    if (consentName && consentType) {
      getConsentText({
        consentName: consentName,
        consentType: consentType,
      }).then((data) => {
        if (data) setConsentObj(data)
      })
    }
  }, [consentName, consentType])

  React.useEffect(() => {
    if (watchForChanges) {
      watch(name)
    }
  }, [watchForChanges])

  const [showConsentCheckbox, setShowConsentCheckbox] = React.useState(true)

  const currentValue = getValues(name)
  React.useEffect(() => {
    if (currentValue && consentName && consentType && name === 'email') {
      getExistingConsent({ email: currentValue, consentName, consentType })
        .then((isConsented) => {
          setShowConsentCheckbox(!isConsented)
        })
        .catch(() => {
          setShowConsentCheckbox(true)
        })
    }
  }, [currentValue])

  const variantPath = (element) => `elements.form.variants.${variant}.${element}`
  const ref = validation ? register(validation) : register
  const props = { name, placeholder, errors, setValue, options, uppercase, inputSize }
  const getLinks = {
    termsAndConditions: links?.termsAndConditions,
    privacyPolicy: links?.privacyPolicy,
  }

  const textField =
    (type === 'text' || type === 'redirect') &&
    text.replace(/\$\{([a-z]+)\}/gi, (original, match) => {
      return match in getLinks ? getLinks[match] : original
    })

  // We also support named validation functions
  if (typeof customFunction === 'object') {
    const validateFunctions = {}
    let hasValidationFunctions = false
    for (const key in customFunction) {
      if (customFunctions[key]) {
        // For each custom function used in the data, generate the corresponding validation function

        validateFunctions[key] = customFunctions[key]({ ...customFunction[key], getValues })
        hasValidationFunctions = true
      }
    }
    if (hasValidationFunctions) {
      // See Validate option in https://react-hook-form.com/v5/api/#register

      validation.validate = validateFunctions
    }
  }

  if (customMask && masks[customMask] && customMask !== 'currency' && customMask !== 'numeric') {
    mask = masks[customMask]
  }

  const onChange = (e) => {
    const { value } = e.target

    if (customMask === 'currency') {
      const currencyString = formatStringToLocale(value)
      return setValue(name, currencyString)
    }

    if (customMask === 'numeric') {
      const currentValue = isValidNumber(value) ? value : value.slice(0, -1)
      return setValue(name, currentValue)
    }

    setValue(name, value)
  }

  const onKeyDown = (e) => {
    if (e.keyCode !== 13) return
    e.preventDefault()
    return onKeyDownFunction?.()
  }

  const handleTrackInteraction = async (e) => {
    const fieldName = e.target.name
    if (fieldTracking) {
      await fieldTracking(fieldName)
    }
  }

  const displayLabel = (label) => {
    return (
      <label sx={{ variant: variantPath('label') }} htmlFor={label}>
        {label}
      </label>
    )
  }

  const displayDescription = (description) => {
    const journeyVariant = variant === 'journey'
    const sectionNameVariant = sectionTitle && journeyVariant && index !== 0

    return (
      <p
        sx={{
          variant: sectionNameVariant
            ? variantPath('description.sectionName')
            : variantPath('description'),
        }}
      >
        {description}
      </p>
    )
  }

  if (errors[name]) {
    trackComponent('Form Event', 'error', { [name]: errors[name].message })
    trackInteraction(
      'GA Validation Error Triggered',
      'Site Interaction',
      `${name}:${errors[name].message}`,
    )
  }

  return (
    <React.Fragment>
      <If condition={isVisible}>
        <If condition={type === 'address-lookup-uswitch'}>
          <div sx={{ variant: variantPath('input.wrapper') }}>
            {label && displayLabel(label)}
            <AddressHistoryUswitch
              minimumRequiredYears={minimumRequiredYears}
              name={name}
              lookup={addressLookup}
              label={title}
              ref={ref}
              error={errors[name]}
              formVariant={variant}
              defaultValue={defaultValues?.addresses && JSON.parse(defaultValues?.addresses)}
              handleTrackInteraction={handleTrackInteraction}
            />

            <If condition={subtext}>
              <p sx={{ textAlign: 'left' }}>{subtext}</p>
            </If>
          </div>
        </If>

        <If condition={type === 'address-input'}>
          {label && displayLabel(label)}
          <AddressInput
            name={name}
            lookup={addressLookup}
            ref={ref}
            error={errors[name]}
            formVariant={variant}
          />

          <If condition={subtext}>
            <p sx={{ textAlign: 'left' }}>{subtext}</p>
          </If>
        </If>

        <If condition={type === 'hidden'}>
          <input type='hidden' name={name} value={value} ref={ref} />
        </If>

        <If condition={type === 'dropdown'}>
          <div
            sx={{
              variant: isSingleRow ? variantPath('input.singleRow') : variantPath('input.wrapper'),
            }}
          >
            {label && displayLabel(label)}
            {description && displayDescription(description)}
            <div
              sx={{
                variant: variantPath('wrapper.base'),
                select: {
                  borderWidth: 1,
                },
              }}
            >
              <SimpleDropDown
                name={name}
                options={options}
                placeholder={placeholder}
                hasError={errors[name]}
                ref={ref}
                handleTrackInteraction={handleTrackInteraction}
                isSingleRow={isSingleRow}
              />
            </div>
            <If condition={errors[name]}>
              <InputAlert type='alert' sx={{ variant: variantPath('input.error') }}>
                {errors[name].message}
              </InputAlert>
            </If>
            {subtext && <p sx={{ textAlign: 'left' }}>{subtext}</p>}
          </div>
        </If>

        <If condition={type === 'wrapper'}>
          <div
            sx={{
              variant: visibleIf
                ? variantPath('input.wrapper.conditional')
                : variantPath('input.wrapper'),
            }}
          >
            {label && displayLabel(label)}
            <div
              sx={{
                display: 'flex',
                justifyContent: isSingleRow ? 'space-between' : 'space-around',
                flexDirection: ['column', isSingleRow ? 'row' : 'column'],
              }}
            >
              <For each='child' of={children} index='key'>
                <div
                  sx={{
                    width: '100%',
                    marginTop: isSingleRow ? 0 : 'xs',
                    '&:first-of-type': { marginTop: 0 },
                  }}
                  key={key}
                >
                  <FormStepField
                    {...{
                      field: { ...child },
                      register,
                      errors,
                      setValue,
                      getValues,
                      variant,
                      watch,
                      goToPage,
                      updateCompletionState,
                      onKeyDownFunction,
                      isChild: true,
                      addressLookup,
                      defaultValues,
                      fieldTracking,
                    }}
                  />
                </div>
              </For>
            </div>
          </div>
        </If>

        <If condition={type === 'radio-vertical'}>
          <Field stack={true}>
            <RadioGroup stack={true} column={true}>
              {label && displayLabel(label)}
              {description && displayDescription(description)}
              {options.map(({ label, value }, i) => (
                <div className={'radio-button'} key={i}>
                  <label
                    className={classNames(
                      { 'radio-checked': value === getValues(name) },
                      { center: false },
                    )}
                  >
                    <input
                      className={'left'}
                      type='radio'
                      name={name}
                      id={name}
                      value={value}
                      ref={ref}
                      onClick={handleTrackInteraction}
                    />
                    {label}
                  </label>
                </div>
              ))}
            </RadioGroup>
            <If condition={errors[name]}>
              <InputAlert
                type='alert'
                sx={{ variant: variantPath('input.error'), position: 'relative', top: 0 }}
              >
                {errors[name].message}
              </InputAlert>
            </If>
          </Field>
        </If>

        <If condition={type === 'input'}>
          <div
            sx={{
              variant: isSingleRow ? variantPath('input.singleRow') : variantPath('input.wrapper'),
            }}
          >
            {label && displayLabel(label)}
            {description && displayDescription(description)}
            <div sx={{ variant: variantPath('wrapper.base') }}>
              <Choose>
                <When condition={mask}>
                  <InputMask
                    name={name}
                    type={type}
                    maskChar={0}
                    onChange={onChange}
                    mask={mask}
                    onKeyDown={onKeyDown}
                  >
                    {(inputProps) => (
                      <SimpleInput
                        prefix={prefix}
                        suffix={suffix}
                        ref={ref}
                        {...inputProps}
                        hasError={errors[name]}
                        name={name}
                        placeholder={props.placeholder}
                        onChange={onChange}
                        onKeyDown={onKeyDown}
                        onBlur={handleTrackInteraction}
                      />
                    )}
                  </InputMask>
                </When>
                <Otherwise>
                  <SimpleInput
                    prefix={prefix}
                    suffix={suffix}
                    ref={ref}
                    hasError={errors[name]}
                    onChange={onChange}
                    name={name}
                    type={type}
                    placeholder={props.placeholder}
                    onKeyDown={onKeyDown}
                    onBlur={handleTrackInteraction}
                  />
                </Otherwise>
              </Choose>

              {subtext && <p sx={{ textAlign: 'left' }}>{subtext}</p>}
              {consentObj && showConsentCheckbox && (
                <React.Fragment>
                  {fieldVariant === 'consent' && (
                    <p sx={{ fontWeight: 'bold', mt: 'xl' }}>Keep in touch with us?</p>
                  )}
                  <div
                    sx={{
                      variant: 'elements.input.consent.wrapper',
                      marginTop: 'sm',
                      textAlign: 'left',
                    }}
                  >
                    {fieldVariant === 'consentMoney' && (
                      <p sx={{ variant: 'elements.input.consent.question' }}>
                        Want to stay in touch?
                      </p>
                    )}
                    <Checkbox
                      defaultChecked={false}
                      label={consentObj.text}
                      name={`${name}-checkbox`}
                      ref={register}
                      onBlur={handleTrackInteraction}
                      sx={{
                        variant: 'elements.input.checkbox.variants.consent',
                      }}
                    >
                      <div>
                        <p sx={{ variant: 'elements.input.consent.text', my: '0' }}>
                          {consentObj.text}
                        </p>
                        {fieldVariant === 'consent' && (
                          <small>
                            You can unsubscribe at any time.{' '}
                            <a href={privacyPolicyLink?.href || '/'} sx={{ color: 'grey-80' }}>
                              {privacyPolicyLink?.name || 'Privacy policy'}
                            </a>
                          </small>
                        )}
                      </div>
                    </Checkbox>
                  </div>
                </React.Fragment>
              )}
              <If condition={errors[name]}>
                <InputAlert
                  type='alert'
                  sx={{ pl: '0', pr: '0', variant: variantPath('input.error') }}
                >
                  {errors[name].message}
                </InputAlert>
              </If>
            </div>
          </div>
        </If>

        <If condition={type === 'text'}>
          <div sx={{ variant: variantPath('input.wrapper'), paddingRight: isChild ? 120 : 0 }}>
            <label sx={{ variant: variantPath('label') }}>{label}</label>
            <If condition={tooltip}>
              <Tooltip content={textField} position='bottom'>
                <span>
                  <Icon glyph='information-circle' size='24' /> {tooltipText}
                </span>
              </Tooltip>
            </If>
            <If condition={!tooltip}>
              <div sx={{ pointerEvents: 'auto' }} dangerouslySetInnerHTML={{ __html: textField }} />
            </If>
          </div>
        </If>

        <If condition={type === 'title'}>
          <h2
            sx={{
              marginY: 20,
              fontWeight: 700,
              fontSize: 36,
              mr: ['md', 'xl', 'xl'],
              variant: `elements.form-section.default.elements.heading`,
            }}
          >
            {text}
          </h2>
        </If>
      </If>
    </React.Fragment>
  )
}

export default FormStepField
